/// <reference path='OSFManifest.ts' />
/// <reference path='OSFConstants.ts' />
/// <reference path='ParserUtility.ts' />
/// <reference path='RequirementsChecker.ts' />
/// <reference path='../../../jscript/AddinCommandsManifestParser.ts' />
/// <reference path='../../../jscript/officeutil/officeutil.ts' />

namespace OSF {
  export namespace Manifest {
    export class FormSetting {
      SourceLocation: string[];
      RequestedHeight: number;
    }

    // OSF.Manifest.Manifest parses Word, Excel and PowerPoint manifests.  This class is used for translation between the legacy
    // xml manifest and the new metaos json manifest.  It will expose any needed data from the base class that will be used by
    // the Converter class to populate the new metaos manifest.
    // Currently this class is not used by production code but only by this mos/converter.
    export class OfficeAppManifest extends OSF.Manifest.Manifest {
      _namespace: string;
      _hostType: string;
      _formSettings: FormSetting[][];
      _disableEntityHighlighting?: boolean;
      _converterLogging?: boolean;

      constructor(
        payload: string,
        uiLocale: string,
        converterLogging?: boolean,
        solRef?: OfficeExt.OsfSolutionReference,
      ) {
        super(payload, uiLocale, OSF.PayloadType.OfficeAddin, solRef);
        this._converterLogging = converterLogging;

        if (this._target === OSF.OfficeAppType.MailApp) {
          this._namespace = OSF.ParserStrings.Mailappor;
        } else if (this._target == OSF.OfficeAppType.TaskPaneApp) {
          this._namespace = "ov:";
        } else if (this._target == OSF.OfficeAppType.ContentApp) {
          this._namespace = "c:";
        }

        if (this._target === OSF.OfficeAppType.MailApp || this._target === OSF.OfficeAppType.TaskPaneApp) {
          this._parseOverrideCore();
        }
      }

      isForOfficeAppManifest() {
        return true;
      }
      converterLogging(): boolean {
        return this._converterLogging == true;
      }

      _parseExtensionSettings() {
        if (this._target == OSF.OfficeAppType.MailApp) {
          this._parseFormSettings();
          this._parseDisableEntityHighlighting();
        } else {
          super._parseExtensionSettings();
        }
      }

      _parseFormSettings(): void {
        const formFactorSettings: string[] = ["DesktopSettings", "TabletSettings", "PhoneSettings"];

        let officeAppNode = this._xmlProcessor.selectSingleNode(ParserStrings.OOfficeApp);
        let formNodes = OSF.SelectChildNodes(this._xmlProcessor, "o:" + ParserStrings.Form, officeAppNode, [
          "o:" + ParserStrings.FormSettings,
        ]);
        this._formSettings = [];

        for (const formNode of formNodes) {
          const formType: string = this.getNodeXsiType(formNode);
          if (formType == null) {
            throw OsfMsAjaxFactory.msAjaxError.argument("Form node without a type.");
          }

          this._formSettings[formType] = {};

          for (let ff of formFactorSettings) {
            let settingsNode = this._xmlProcessor.selectSingleNode("o:" + ff, formNode);
            if (settingsNode != null) {
              this._formSettings[formType][ff] = {};

              let sourceLocationNode = this._xmlProcessor.selectSingleNode(
                "o:" + ParserStrings.SourceLocation,
                settingsNode,
              );
              if (sourceLocationNode != null) {
                this._formSettings[formType][ff].SourceLocation = {};

                let addSourceLocationCallback = (locale: string, value: string) => {
                  this._formSettings[formType][ff].SourceLocation[locale] = value;
                };
                this._parseLocaleAwareSettings(sourceLocationNode, addSourceLocationCallback);
              }

              let requestedHeightNode = this._xmlProcessor.selectSingleNode(
                "o:" + ParserStrings.RequestedHeight,
                settingsNode,
              );
              if (requestedHeightNode != null) {
                this._formSettings[formType][ff].RequestedHeight = this._xmlProcessor.getNodeValue(requestedHeightNode);
              }
            }
          }
        }
      }

      _parseDisableEntityHighlighting(): void {
        let officeAppNode = this._xmlProcessor.selectSingleNode(ParserStrings.OOfficeApp);
        let disableEntityHighlightingNode = this._xmlProcessor.selectSingleNode(
          "o:DisableEntityHighlighting",
          officeAppNode,
        );
        if (disableEntityHighlightingNode != null) {
          let value: string = this._xmlProcessor.getNodeValue(disableEntityHighlightingNode).toLowerCase();
          this._disableEntityHighlighting = value === "true" || value === "1";
        }
      }

      _parseOverrideCore() {
        let officeAppNode = this._xmlProcessor.selectSingleNode("o:OfficeApp");
        let node: any;

        // Com2Web equivalent addin info
        if (this._manifestSchemaVersion === OSF.ManifestSchemaVersion["1.1"]) {
          let ns: string = this._namespace;
          node = this._xmlProcessor.selectSingleNode(this._namespace + "VersionOverrides", officeAppNode);

          // Outlook's equivalent add-ins node is defined in VersionOverrides V1.1, not in V1.0.
          if (this._target === OSF.OfficeAppType.MailApp) {
            let versionOverridesNodePath: string[] = OSF.GetVersionOverridesPath(
              this._xmlProcessor,
              ns,
              OSF.VersionOverridesNumber.V1_1,
            );
            node = OSF.SelectSingleNode(
              this._xmlProcessor,
              officeAppNode,
              versionOverridesNodePath,
              true /*isStrictPath*/,
            );
            ns = OSF.GetVersionOverridesNamespace(ns, OSF.VersionOverridesNumber.V1_1);
          }

          if (node != null) {
            let equivalentAddinsNode = this._xmlProcessor.selectSingleNode(ns + "EquivalentAddins", node);
            this._equivalentAddins = this._parseEquivalentAddins(equivalentAddinsNode, ns);
          }
        }

        if (this._manifestSchemaVersion === OSF.ManifestSchemaVersion["1.1"]) {
          // Let's try to find VersonOverrides nodes, V1_0, V1_1, V1_2, ..., in this order one by one, until the desired node does not exist.
          for (const v in OSF.VersionOverridesNumber) {
            // Since the enum will be iterated twice, one for values as numbers and one for keys as string, let's only do the values.
            let versionOverridesNumber: number = Number(v);
            if (isNaN(versionOverridesNumber) || versionOverridesNumber == OSF.VersionOverridesNumber.Max) {
              // also ignore 'Max'
              continue;
            }

            // Get the VersionOverrides node. Handle it if exists or otherwise stop.
            let versionOverridesNodePath: string[] = OSF.GetVersionOverridesPath(
              this._xmlProcessor,
              this._namespace,
              versionOverridesNumber,
            );
            let versionOverridesNode: any = OSF.SelectSingleNode(
              this._xmlProcessor,
              officeAppNode,
              versionOverridesNodePath,
              true /*isStrictPath*/,
            );
            if (versionOverridesNode == null) {
              break;
            }

            this._parseRuntimesAndVersionOverrides(versionOverridesNode, versionOverridesNumber);
          }
        }
      }

      _parseRuntimesAndVersionOverrides(
        versionOverridesNode: any,
        versionOverridesNumber: OSF.VersionOverridesNumber,
      ): void {
        let versionOverridesString: string = this.getNodeXsiType(versionOverridesNode);
        if (versionOverridesString === OSF.GetVersionOverridesString(versionOverridesNumber)) {
          let ns: string = OSF.GetVersionOverridesNamespace(this._namespace, versionOverridesNumber);
          let hostsNode = this._xmlProcessor.selectSingleNode(ns + "Hosts", versionOverridesNode);
          if (hostsNode != null) {
            let hostNodes = this._xmlProcessor.selectNodes(ns + "Host", hostsNode);
            let len = hostNodes == null ? 0 : hostNodes.length;
            for (let i = 0; i < len; i++) {
              this._hostType = this.getNodeXsiType(hostNodes[i]);

              let versionOverrides: object;

              if (this._hostType !== "MailHost") {
                // For WXP, there is not separate index and it just goes into the main override
                // We start off with the colletion of values that have already been parsed by the base parser
                versionOverrides = this._versionOverrides[this._hostType];
              }

              if (this._versionOverrides[this._hostType] == null) {
                this._versionOverrides[this._hostType] = {};
              }

              if (this._versionOverrides[this._hostType][versionOverridesNumber] == null) {
                versionOverrides = {};
                versionOverrides["_addinCommandsExtensionPoints"] = {};
                versionOverrides["_functionFileResid"] = {};
                versionOverrides["_supportsSharedFolders"] = {};
              }

              versionOverrides["_webApplicationInfo"] = {};
              this._parseWebApplicationInfo(versionOverrides["_webApplicationInfo"], versionOverridesNode, ns);

              let connectedServiceControlsScopesNode = OSF.SelectSingleNode(
                this._xmlProcessor,
                versionOverridesNode,
                [ns + "ConnectedServiceControls", ns + "Scopes"],
                true /*isStrictPath*/,
              );
              versionOverrides["_connectedServiceControlsScopes"] = this._parseWebApplicationScopes(
                connectedServiceControlsScopesNode,
                ns,
              );

              let extendedPermissionsNode = this._xmlProcessor.selectSingleNode(
                ns + "ExtendedPermissions",
                versionOverridesNode,
              );
              versionOverrides["_extendedPermissions"] = this._parseExtendedPermissions(extendedPermissionsNode, ns);

              let requirementsNode = this._xmlProcessor.selectSingleNode(ns + "Requirements", versionOverridesNode);
              if (requirementsNode != null) {
                versionOverrides["_requirements"] = this._parseRequirements(requirementsNode, "bt:");
              }

              let runtimesNode = this._xmlProcessor.selectSingleNode(ns + "Runtimes", hostNodes[i]);
              if (runtimesNode) {
                versionOverrides["_runtimes"] = this._parseRuntimes(runtimesNode, ns);
              }

              let formFactors: string[] = ["DesktopFormFactor", "MobileFormFactor"];
              let formFactorIrrelevantPropertiesObtained: boolean = false;
              for (const formFactor of formFactors) {
                let addinCommandVersion = this._parseVersionOverridesFormFactor(
                  this._hostType,
                  versionOverridesNumber,
                  formFactor,
                );
                versionOverrides["_addinCommandsExtensionPoints"][formFactor] = addinCommandVersion.ExtensionPoints;
                versionOverrides["_functionFileResid"][formFactor] = addinCommandVersion.functionFileResid;
                versionOverrides["_supportsSharedFolders"][formFactor] = addinCommandVersion.supportsSharedFolders;
                if (!formFactorIrrelevantPropertiesObtained) {
                  versionOverrides["_description"] = addinCommandVersion.description;
                  versionOverrides["_resources"] = addinCommandVersion.resources;
                  formFactorIrrelevantPropertiesObtained = true;
                }
              }

              // Now assign these back once they've been filled up with information
              if (this._hostType === "MailHost") {
                this._versionOverrides[this._hostType][versionOverridesNumber] = versionOverrides;
              } else {
                // For WXP, there is not separate index (100, 101 etc.) and it just goes into the main override
                // We alto take some relevant fields from the _versionOverrides that was parsed by the base OSFManifest
                // and merge them into the values parsed here.
                versionOverrides["_getStartedNode"] = this._versionOverrides[this._hostType]["_getStartedNode"];
                versionOverrides["_customFunctionExtensionPoint"] =
                  this._versionOverrides[this._hostType]["_customFunctionExtensionPoint"];
                this._versionOverrides[this._hostType] = versionOverrides;
              }

              break;
            }
          }
        }
      }

      _parseExtendedPermissions(extendedPermissionsNode: any, nodeNamespace: string): string[] {
        let extendedPermissions: string[] = [];
        if (extendedPermissionsNode != null) {
          let extendedPermissionNodes = this._xmlProcessor.selectNodes(
            nodeNamespace + "ExtendedPermission",
            extendedPermissionsNode,
          );
          for (const extendedPermissionNode of extendedPermissionNodes) {
            extendedPermissions.push(this._xmlProcessor.getNodeValue(extendedPermissionNode));
          }
        }
        return extendedPermissions;
      }

      _parseRuntimes(runtimesNode: any, nodeNamespace?: string): OSF.Manifest.Runtimes {
        let runtimes = new OSF.Manifest.Runtimes();
        let ns: string = nodeNamespace == null ? this._namespace : nodeNamespace!;
        if (runtimesNode != null) {
          let runtimeNodes = this._xmlProcessor.selectNodes(ns + "Runtime", runtimesNode);
          if (runtimeNodes != null) {
            for (let i = 0; i < runtimeNodes.length; i++) {
              let runtime = this._parseRuntime(runtimeNodes[i], ns);
              runtimes.add(runtime);
            }
          }
        }
        return runtimes;
      }

      _parseRuntime(runtimeNode: any, nodeNamespace?: string): any {
        if (runtimeNode == null) {
          return null;
        }
        let runtime = new OSF.Manifest.Runtime();
        runtime.Resid = runtimeNode.getAttribute("resid");
        if (runtimeNode.getAttribute("lifetime") == "long") {
          runtime.LifeTime = RuntimeLifeTime.Long;
        } else {
          runtime.LifeTime = RuntimeLifeTime.Short;
        }

        let ns: string = nodeNamespace == null ? this._namespace : nodeNamespace!;
        let overrideNodes = this._xmlProcessor.selectNodes(ns + "Override", runtimeNode);
        if (overrideNodes != null) {
          for (let i = 0; i < overrideNodes.length; i++) {
            let override = new RuntimeOverride();
            override.Resid = overrideNodes[i].getAttribute("resid");
            override.Type = overrideNodes[i].getAttribute("type");
            runtime.Overrides.push(override);
          }
        }
        return runtime;
      }

      _parseVersionOverridesFormFactor(
        hostType: string,
        versionOverridesNumber: OSF.VersionOverridesNumber,
        formFactor: string,
      ): any {
        // Parse add-in commands and cache it.
        let addinCommandsParser = new OfficeExt.Parser.AddinCommandsManifestParser(
          hostType,
          formFactor,
          this._namespace,
        );
        let errorManager = {
          setErrorMessageForAddin: (errorMessage) => {
            OsfMsAjaxFactory.msAjaxDebug.trace("Error parsing app manifest: " + errorMessage);
          },
        };

        let entitlement: OfficeExt.AddinEntitlement = this._getEntitlement(this._solutionRef);

        const addinCommands = addinCommandsParser.parseExtensions(
          entitlement,
          this as any,
          errorManager,
          versionOverridesNumber,
        ) as OfficeExt.Parser.VersionOverrides;
        return addinCommands;
      }

      _getVersionOverrides(versionOverridesNumber: OSF.VersionOverridesNumber): any {
        // The base parser for WXP puts the properties at this._versionOverrides[this._hostType] and do not use versionOverrides Number like Outlook.
        if (this._versionOverrides[this._hostType] != null && versionOverridesNumber == null) {
          return this._versionOverrides[this._hostType];
        } else if (this._versionOverrides[this._hostType] != null) {
          return this._versionOverrides[this._hostType][versionOverridesNumber];
        } else {
          return null;
        }
      }

      getLocalesSeen() {
        return this._localesSeen;
      }

      getFormSettings(): FormSetting[][] {
        return this._formSettings;
      }

      getDisableEntityHighlighting(): boolean | undefined {
        return this._disableEntityHighlighting;
      }

      getVersionOverridesDescription(versionOverridesNumber: OSF.VersionOverridesNumber): string | undefined {
        return this._getVersionOverrides(versionOverridesNumber)?._description;
      }

      getVersionOverridesRequirements(versionOverridesNumber: OSF.VersionOverridesNumber): any {
        return this._getVersionOverrides(versionOverridesNumber)?._requirements;
      }

      getWebApplicationInfo(versionOverridesNumber: OSF.VersionOverridesNumber): any {
        return this._getVersionOverrides(versionOverridesNumber)?._webApplicationInfo;
      }

      getConnectedServiceControlsScopes(versionOverridesNumber: OSF.VersionOverridesNumber): string[] | undefined {
        return this._getVersionOverrides(versionOverridesNumber)?._connectedServiceControlsScopes;
      }

      getExtendedPermissions(versionOverridesNumber: OSF.VersionOverridesNumber): string[] | undefined {
        return this._getVersionOverrides(versionOverridesNumber)?._extendedPermissions;
      }

      getRuntimes(versionOverridesNumber: OSF.VersionOverridesNumber): any {
        return this._getVersionOverrides(versionOverridesNumber)?._runtimes?.getRuntimes();
      }

      getExtensionPoints(versionOverridesNumber: OSF.VersionOverridesNumber, formFactor: string): any {
        return this._getVersionOverrides(versionOverridesNumber)?._addinCommandsExtensionPoints[formFactor];
      }

      getFunctionFileResid(versionOverridesNumber: OSF.VersionOverridesNumber, formFactor: string): string {
        return this._getVersionOverrides(versionOverridesNumber)?._functionFileResid[formFactor] || "";
      }

      getResources(versionOverridesNumber: OSF.VersionOverridesNumber): object {
        return this._getVersionOverrides(versionOverridesNumber)?._resources;
      }

      getSupportsSharedFolders(versionOverridesNumber: OSF.VersionOverridesNumber, formFactor: string): boolean {
        return this._getVersionOverrides(versionOverridesNumber)?._supportsSharedFolders[formFactor] || false;
      }
    }
  }
}
